import { z } from 'zod';

import { defaultVoltageClasses } from '../data/default-voltage-classes';
import { AddressSchema } from '../validators';
import { FeatureFlags } from './FeatureFlags';
import { CollectionFileSchema } from './File.schema';
import { PermissionSchemaProject } from './Permission.schema';
import { ProjectDesignSchema } from './ProjectDesign';
import { DesignerSubscription, DesignerSubscriptionSchema } from './Subscription';
import { TeamInfoSchema } from './Team';
import { UserSchema } from './User.schema';
import { VoltageClassSchema } from './VoltageClass.schema';

export const ProjectStatusSchema = z.enum(['progress', 'review', 'done', 'archived', 'deleted']);

export const ProjectTemplateSchema = z.object({
    profile: z.string().nullable().default(null),
    components: z.array(z.string()).nullable().default(null),
    indicators: z
        .object({
            storage: z.number().default(0),
            grid: z.number().default(0),
            generation: z.number().default(0),
            load: z.number().default(0),
            billOfMaterials: z.number().default(0),
        })
        .nullable()
        .default(null),
});

export const ProjectCollaboratorSchema = z.object({
    id: z.string(),
    project: z.string(),
    user: z.string(),
    permissions: PermissionSchemaProject.array().optional().default([]),
});

export const ProjectUserSchema = z.object({
    id: z.string(),
    project: z.string(),
    user: z.string(),
});

export const ProjectAccessRequestSchema = z.object({
    id: z.string(),
    project: z.string(),
    status: z.enum(['pending', 'approved', 'denied']),
    permissions: PermissionSchemaProject.array().optional().default([]),
    handledBy: z.string().nullable().optional(),
    handledAt: z.string().nullable().optional(),
    updatedAt: z.string(),
    createdBy: z.string(),
    createdAt: z.string(),
});

export const ProjectSchema = z.object({
    id: z.string(),
    slug: z.string().default(''),
    name: z.string().min(3, 'Name must be at least 3 characters long'),
    description: z.string().default(''),
    customer: z.string().default(''),
    tags: z.string().array().default([]),
    location: AddressSchema.nullable(),
    designs: ProjectDesignSchema.array(),
    thumbnail: z.string().default('').nullable(),

    status: ProjectStatusSchema,
    statusUpdates: z
        .object({
            id: z.string(),
            status: ProjectStatusSchema,
            date: z.string(),
            user: UserSchema,
        })
        .array(),

    liveDataEnabled: z.boolean().default(false),
    secret: z.string().default(''),

    createdAt: z.date(),
    updatedAt: z.date().nullable(),

    createdBy: UserSchema,
    updatedBy: UserSchema.nullable(),

    permissions: PermissionSchemaProject.array(),
    permissionsAs: z.enum(['anonymous', 'user', 'collaborator', 'company']),
    teamDesignerSubscription: DesignerSubscriptionSchema.optional().default(DesignerSubscription.NONE),
    teamFlags: z.array(z.nativeEnum(FeatureFlags)).default([]),
    everyonePermissions: PermissionSchemaProject.array(),

    team: TeamInfoSchema.shape.id,

    files: CollectionFileSchema.array().default([]),

    theme: z
        .object({
            iconStyle: z.enum(['straight', 'rounded', 'rounded-large', 'circle']).default('rounded'),
        })
        .default({}),

    wireSizing: z
        .object({
            standard: z.enum(['IEC', 'NEC']).default('IEC').catch('IEC'),
            voltageDrop: z
                .number()
                .min(0)
                .max(1)
                .default(0.03)
                .catch(() => 0.03),
            automaticCharging: z.boolean().default(false).catch(false),
            cover: z
                .object({
                    parties: z
                        .object({
                            name: z.string().default(''),
                            description: z.string().default(''),
                        })
                        .array()
                        .default([
                            {
                                name: '',
                                description: '',
                            },
                            {
                                name: '',
                                description: '',
                            },
                            {
                                name: '',
                                description: '',
                            },
                        ]),
                })
                .default({}),
        })
        .default({}),

    collaborators: z
        .object({
            users: z
                .object({
                    docs: z.string().array().optional().default([]),
                })
                .optional()
                .default({}),
        })
        .optional()
        .default({}),

    visibility: z.enum(['private', 'public', 'marketplace']).default('private'),

    isReferenceDesign: z.boolean().default(false),
    template: ProjectTemplateSchema.optional().default({}),
    reference: z
        .object({
            profile: z.string().nullable().optional().default(null),
            design: z.string().optional().nullable().default(null),
        })
        .optional()
        .default({}),

    voltageClasses: z
        .object({
            AC: VoltageClassSchema.array().default([]),
            DC: VoltageClassSchema.array().default([]),
        })
        .default(defaultVoltageClasses),
});

export const CreateProjectMetadataSchema = z.object({
    name: z.string().optional(),
    startFromExistingProject: z.string().optional(),
    referenceComponent: z.string().optional(),
    manufacturer: z.string().optional(),
    isReferenceDesign: z.preprocess((value) => value === 'true', z.boolean()).optional(),
});

export const ProjectTeaserSchema = ProjectSchema.pick({
    id: true,
    name: true,
    customer: true,
    tags: true,
    isReferenceDesign: true,
    visibility: true,
    createdAt: true,
    updatedAt: true,
}).extend({
    team: z.string().nullable(),
    createdBy: z.string().nullable(),
    updatedBy: z.string().nullable(),
    thumbnail: z.string().nullable(),
    combinedSlug: z.string(),
});
